import React, { useEffect, useState } from 'react';

/**
 * Minimal cookie/tracking consent banner — opt-in for non-essential cookies.
 *
 * Stores the choice in localStorage under `cookieConsent` so we don't
 * re-prompt every visit. Two states: `accepted` (analytics + marketing OK)
 * or `rejected` (only strictly-necessary cookies allowed).
 *
 * Other code (analytics initializers, Meta Pixel, etc.) should read
 *   `localStorage.getItem('cookieConsent') === 'accepted'`
 * before firing — and re-check on the `cookieconsent` window event.
 *
 * Compliant with PIPEDA + Quebec Law 25 expectations: prior, informed,
 * granular-where-possible, easy to revoke (the banner reappears via a
 * footer "Manage cookies" link that calls window.dispatchEvent(new Event('cookieconsent:show'))).
 */
const KEY = 'cookieConsent';

export default function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Hide entirely when rendered inside the page builder preview iframe.
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('builder') === '1' && qs.get('overlays') !== '1') return;
    if (!localStorage.getItem(KEY)) setShow(true);
    const onShow = () => setShow(true);
    window.addEventListener('cookieconsent:show', onShow);
    return () => window.removeEventListener('cookieconsent:show', onShow);
  }, []);

  if (!show) return null;

  function decide(value) {
    localStorage.setItem(KEY, value);
    window.dispatchEvent(new CustomEvent('cookieconsent:change', { detail: value }));
    setShow(false);
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      style={{
        position: 'fixed', bottom: 16, left: 16, right: 16,
        maxWidth: 720, margin: '0 auto',
        background: '#111827', color: '#fff',
        padding: '14px 18px', borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
        display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
        zIndex: 9999, fontSize: 14,
      }}
    >
      <div style={{ flex: '1 1 280px', lineHeight: 1.5 }}>
        We use cookies to keep you signed in and process orders. With your consent we'd also like to use them
        to improve the site and personalize content.{' '}
        <a href="/privacy" style={{ color: '#ffde00' }}>Read our privacy policy →</a>
      </div>
      <button
        onClick={() => decide('rejected')}
        style={{
          background: 'transparent', color: '#fff',
          border: '1px solid rgba(255,255,255,0.4)',
          padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
        }}
      >Strictly necessary only</button>
      <button
        onClick={() => decide('accepted')}
        style={{
          background: '#ffde00', color: '#111',
          border: 'none', fontWeight: 700,
          padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
        }}
      >Accept all</button>
    </div>
  );
}
